import { useState, useEffect, useRef } from 'react';
import { liveResultsService } from '../../_services/liveResultsService';

export const useLiveResults = (slug) => {
  const [resultados, setResultados] = useState([]);
  const [resultadosGeneral, setResultadosGeneral] = useState([]);

  const [disponible, setDisponible] = useState(false);
  const [disponibleGeneral, setDisponibleGeneral] = useState(false);

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [actualizadoEn, setActualizadoEn] = useState(null);
  const [generalActualizadoEn, setGeneralActualizadoEn] = useState(null);

  const [cantidad, setCantidad] = useState(0);
  const [cantidadGeneral, setCantidadGeneral] = useState(0);

  const [conexion, setConexion] = useState('conectando');

  const eventSourceRef = useRef(null);

  useEffect(() => {
    if (!slug) {
      setResultados([]);
      setResultadosGeneral([]);
      setDisponible(false);
      setDisponibleGeneral(false);
      setCargando(false);
      setError('No se especificó la competencia.');
      setConexion('desconectado');
      return undefined;
    }

    let cancelled = false;
    let consultando = false;
    let consultaPendiente = false;

    setResultados([]);
    setResultadosGeneral([]);
    setDisponible(false);
    setDisponibleGeneral(false);
    setCargando(true);
    setError(null);
    setActualizadoEn(null);
    setGeneralActualizadoEn(null);
    setCantidad(0);
    setCantidadGeneral(0);
    setConexion('conectando');

    const consultarResultados = async () => {
      if (cancelled) return;

      if (consultando) {
        consultaPendiente = true;
        return;
      }

      consultando = true;

      try {
        const data = await liveResultsService.getResults(slug);

        if (cancelled) return;

        // ==========================================
        // Clasificación por categorías
        // ==========================================
        setDisponible(Boolean(data?.disponible));

        if (Array.isArray(data?.resultados)) {
          setResultados(data.resultados);
        }

        setCantidad(
          Number.isFinite(Number(data?.cantidad))
            ? Number(data.cantidad)
            : 0
        );

        setActualizadoEn(data?.actualizadoEn || null);

        // ==========================================
        // Clasificación general
        // ==========================================
        setDisponibleGeneral(Boolean(data?.disponibleGeneral));

        if (Array.isArray(data?.resultadosGeneral)) {
          setResultadosGeneral(data.resultadosGeneral);
        }

        setCantidadGeneral(
          Number.isFinite(Number(data?.cantidadGeneral))
            ? Number(data.cantidadGeneral)
            : 0
        );

        setGeneralActualizadoEn(data?.generalActualizadoEn || null);

        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error ||
            err?.response?.data?.mensaje ||
            err?.message ||
            'No se pudieron consultar los resultados en vivo.'
          );
        }
      } finally {
        consultando = false;

        if (!cancelled) {
          setCargando(false);

          if (consultaPendiente) {
            consultaPendiente = false;
            consultarResultados();
          }
        }
      }
    };

    let eventSource;

    try {
      const streamUrl = liveResultsService.getStreamUrl(slug);

      eventSource = new EventSource(streamUrl);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        if (!cancelled) {
          setConexion('conectado');
        }
      };

      eventSource.addEventListener('ready', () => {
        if (cancelled) return;

        setConexion('conectado');
        consultarResultados();
      });

      eventSource.addEventListener('results-updated', () => {
        if (cancelled) return;

        consultarResultados();
      });

      eventSource.onerror = () => {
        if (cancelled) return;

        setConexion(
          eventSource.readyState === EventSource.CLOSED
            ? 'desconectado'
            : 'reconectando'
        );
      };
    } catch (err) {
      setConexion('desconectado');
      setError(
        err?.message ||
        'No se pudo establecer la conexión de resultados.'
      );
      setCargando(false);
    }

    return () => {
      cancelled = true;

      if (eventSource) {
        eventSource.close();
      }

      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }
    };
  }, [slug]);

  return {
    // Categorías
    resultados,
    disponible,
    cantidad,
    actualizadoEn,

    // General
    resultadosGeneral,
    disponibleGeneral,
    cantidadGeneral,
    generalActualizadoEn,

    // Compartido
    cargando,
    error,
    conexion
  };
};

export default useLiveResults;